import { getApiBaseUrl } from '../config/apiBase';

const DEFAULT_RETRIES = 2;
const RETRY_DELAY_MS = 400;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isNetworkFailure = (error) =>
  error instanceof TypeError &&
  (error.message.includes('fetch') ||
    error.message.includes('NetworkError') ||
    error.message.includes('Failed to fetch'));

const isHtmlBody = (text) => {
  const trimmed = (text || '').trim().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
};

export const buildApiUrl = (path) => {
  if (/^https?:\/\//i.test(path)) return path;
  const base = getApiBaseUrl();
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

export async function apiFetch(path, options = {}, retries = DEFAULT_RETRIES) {
  const url = buildApiUrl(path);
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      if (!isNetworkFailure(error) || attempt === retries) break;
      await sleep(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  if (isNetworkFailure(lastError)) {
    throw new Error(
      'Unable to reach the API server. Make sure the backend is running on port 5000, then refresh.'
    );
  }

  throw lastError || new Error('Network request failed');
}

export async function parseJsonResponse(response, fallbackMessage = 'Request failed') {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (isHtmlBody(text)) {
    throw new Error(
      'Received HTML instead of API JSON. Restart the frontend (npm start) so the API proxy is active, and ensure the backend is on port 5000.'
    );
  }

  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(
        contentType.includes('json')
          ? `${fallbackMessage}: invalid JSON`
          : `${fallbackMessage}: unexpected response (HTTP ${response.status})`
      );
    }
  }

  if (!response.ok) {
    throw new Error(data.message || fallbackMessage);
  }

  return data;
}
